// src/screens/HelpScreen.js
//
// Help tab — explains how to get music into SleepMixBlueprint, why certain songs
// don't show up, and answers common questions. Focused on the "My Music"
// import flow because that's where most users get stuck.

import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  StyleSheet,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Radius, Fonts, Typography } from '../theme';

// ─── Data ─────────────────────────────────────────────────────────────────────

const STEPS = [
  {
    num: '1',
    title: 'Make sure the song is on your phone',
    body: "SleepMixBlueprint can only import files your phone can already see. If the track lives in a streaming app (Spotify, Apple Music, etc.) it's not actually stored on your device — see the FAQ below for why.",
  },
  {
    num: '2',
    title: 'Open the My Music tab',
    body: 'Tap the "My Music" tab at the bottom of the screen.',
  },
  {
    num: '3',
    title: 'Tap "Add music"',
    body: "Your device's file picker will open. If you have iCloud Drive, Google Drive, or Dropbox installed, those appear here too — browse straight to your song.",
  },
  {
    num: '4',
    title: 'Pick your file',
    body: 'Tap the song. SleepMixBlueprint copies it in and it shows up in your library immediately.',
  },
  {
    num: '5',
    title: 'Tag it by mood',
    body: 'Tap Sleep, Study, Focus, or any custom category you create so you can find it fast later.',
  },
];

const FAQS = [
  {
    id: 'streaming',
    icon: 'ban-outline',
    question: "Why can't I import from Spotify or Apple Music?",
    answer:
      "Streaming services use DRM (copy protection) that locks audio inside their own apps. None of them allow a third-party app to pull raw audio out — not just SleepMixBlueprint, but any app. The only way to use that music alongside SleepMixBlueprint is to play it in Spotify or Apple Music at the same time; both apps can run audio simultaneously.",
  },
  {
    id: 'missing',
    icon: 'search-outline',
    question: "My song isn't showing up in the picker",
    answer:
      "The file probably isn't saved to your phone yet. On iPhone: open the Files app and look in Downloads or iCloud Drive. On Android: check the Files or Downloads app. If you bought the track somewhere, look for a \"Save\" or \"Download\" option in that app first. Once it's saved locally, come back and tap \"Add music\" — it'll be there.",
  },
  {
    id: 'icloud',
    icon: 'cloud-outline',
    question: 'How do I import from iCloud Drive, Google Drive, or Dropbox?',
    answer:
      'Make sure the relevant app (Files, Google Drive, or Dropbox) is installed on your phone. When you tap "Add music," those sources appear automatically in the picker. Tap "Browse" at the bottom if you don\'t see them right away, then navigate to your file.',
  },
  {
    id: 'purchase',
    icon: 'musical-note-outline',
    question: 'I bought the song — where is it?',
    answer:
      'iTunes / Apple Music purchases: open the Music app, find the song, and tap the download icon (cloud with arrow) to save it locally. Then import from your Files app. Amazon Music purchases: open Amazon Music → My Music → Downloaded, then use "Share" or "Export" if available. Bandcamp: open your purchase email and download the MP3/FLAC file directly.',
  },
  {
    id: 'reimport',
    icon: 'warning-outline',
    question: 'A track shows a warning icon — what does that mean?',
    answer:
      "The file was moved, deleted, or the temporary import path expired (this can happen after an app restart on some devices). Tap the track and choose \"Re-import\" to locate the file again — your categories and tags are saved and will carry over.",
  },
  {
    id: 'format',
    icon: 'document-outline',
    question: 'What file types work?',
    answer:
      'MP3, AAC (.m4a), WAV, FLAC, and OGG all work. If a file refuses to import or plays silently, try converting it to MP3 first — free tools like VLC (desktop) or an online converter handle that in seconds.',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepCard({ num, title, body }) {
  return (
    <View style={styles.stepCard}>
      <View style={styles.stepNumBubble}>
        <Text style={styles.stepNum}>{num}</Text>
      </View>
      <View style={styles.stepBody}>
        <Text style={styles.stepTitle}>{title}</Text>
        <Text style={styles.stepText}>{body}</Text>
      </View>
    </View>
  );
}

function FaqItem({ icon, question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <Pressable
      onPress={() => setOpen((v) => !v)}
      style={[styles.faqCard, open && styles.faqCardOpen]}
      accessibilityRole="button"
      accessibilityState={{ expanded: open }}
    >
      <View style={styles.faqRow}>
        <View style={styles.faqIconBubble}>
          <Ionicons name={icon} size={18} color={Colors.accent} />
        </View>
        <Text style={styles.faqQuestion}>{question}</Text>
        <Ionicons
          name={open ? 'chevron-up' : 'chevron-down'}
          size={16}
          color={Colors.textMuted}
        />
      </View>
      {open && <Text style={styles.faqAnswer}>{answer}</Text>}
    </Pressable>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function HelpScreen() {
  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      {/* Hero */}
      <View style={styles.hero}>
        <View style={styles.heroIconRow}>
          <View style={styles.heroBadge}>
            <Ionicons name="help-circle-outline" size={18} color={Colors.accent} />
          </View>
          <Text style={styles.eyebrow}>HELP</Text>
        </View>
        <Text style={styles.heroTitle}>Getting your music in</Text>
        <Text style={styles.heroLede}>
          SleepMixBlueprint lets you mix your own tracks alongside ambient sounds. Here's
          how to import music and fix the most common hiccups.
        </Text>
      </View>

      <View style={styles.divider} />

      {/* Step-by-step */}
      <Text style={styles.sectionLabel}>HOW TO IMPORT A SONG</Text>
      <View style={styles.stepList}>
        {STEPS.map((s) => (
          <StepCard key={s.num} {...s} />
        ))}
      </View>

      <View style={styles.divider} />

      {/* FAQ */}
      <Text style={styles.sectionLabel}>COMMON QUESTIONS</Text>
      <View style={styles.faqList}>
        {FAQS.map((f) => (
          <FaqItem key={f.id} {...f} />
        ))}
      </View>

      {/* Footer tip */}
      <View style={styles.tipBox}>
        <Ionicons name="bulb-outline" size={16} color={Colors.accent} style={{ marginTop: 1 }} />
        <Text style={styles.tipText}>
          Tip: once a track is imported you can play it at the same time as any
          ambient layer — rain + your own lo-fi playlist, for example. Just turn
          on both from the Sleep or Study tab.
        </Text>
      </View>

      <View style={styles.divider} />

      {/* Contact & Support */}
      <Text style={styles.sectionLabel}>CONTACT &amp; SUPPORT</Text>
      <View style={styles.contactList}>

        <Pressable
          style={styles.contactRow}
          onPress={() => Linking.openURL('mailto:sleepmixblueprint@gmail.com')}
          accessibilityRole="link"
          accessibilityLabel="Email Support"
        >
          <View style={styles.contactIconBubble}>
            <Ionicons name="mail-outline" size={18} color={Colors.accent} />
          </View>
          <View style={styles.contactTextGroup}>
            <Text style={styles.contactTitle}>Email Support</Text>
            <Text style={styles.contactSub}>sleepmixblueprint@gmail.com</Text>
          </View>
          <Ionicons name="chevron-forward" size={16} color={Colors.textMuted} />
        </Pressable>

        <Pressable
          style={styles.contactRow}
          onPress={() =>
            Linking.openURL(
              'mailto:sleepmixblueprint@gmail.com?subject=SleepMixBlueprint%20Feedback'
            )
          }
          accessibilityRole="link"
          accessibilityLabel="Send Feedback"
        >
          <View style={styles.contactIconBubble}>
            <Ionicons name="chatbubble-ellipses-outline" size={18} color={Colors.accent} />
          </View>
          <View style={styles.contactTextGroup}>
            <Text style={styles.contactTitle}>Send Feedback</Text>
            <Text style={styles.contactSub}>Share ideas or report a bug</Text>
          </View>
          <Ionicons name="chevron-forward" size={16} color={Colors.textMuted} />
        </Pressable>

        <Pressable
          style={[styles.contactRow, styles.contactRowLast]}
          onPress={() => Linking.openURL('https://sleepmixblueprint.com/privacy')}
          accessibilityRole="link"
          accessibilityLabel="Privacy Policy"
        >
          <View style={styles.contactIconBubble}>
            <Ionicons name="shield-checkmark-outline" size={18} color={Colors.accent} />
          </View>
          <View style={styles.contactTextGroup}>
            <Text style={styles.contactTitle}>Privacy Policy</Text>
            <Text style={styles.contactSub}>sleepmixblueprint.com/privacy</Text>
          </View>
          <Ionicons name="chevron-forward" size={16} color={Colors.textMuted} />
        </Pressable>

      </View>
    </ScrollView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: Colors.bg },
  content: { padding: 20, paddingBottom: 48 },

  /* Hero */
  hero: { paddingTop: 8, paddingBottom: 24 },
  heroIconRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 },
  heroBadge: {
    width: 34,
    height: 34,
    borderRadius: Radius.sm,
    backgroundColor: Colors.accent + '22',
    alignItems: 'center',
    justifyContent: 'center',
  },
  eyebrow: { fontFamily: Fonts.bold, fontSize: 11, letterSpacing: 3, color: Colors.accent },
  heroTitle: {
    fontFamily: Fonts.bold,
    fontSize: 22,
    color: Colors.textBright,
    lineHeight: 30,
    marginBottom: 10,
  },
  heroLede: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.textSecondary,
    lineHeight: 22,
  },

  divider: { height: 1, backgroundColor: Colors.border, marginBottom: 20 },

  sectionLabel: {
    fontFamily: Fonts.bold,
    fontSize: 11,
    letterSpacing: 2,
    color: Colors.textMuted,
    marginBottom: 14,
  },

  /* Steps */
  stepList: { gap: 10, marginBottom: 28 },
  stepCard: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
  },
  stepNumBubble: {
    width: 32,
    height: 32,
    borderRadius: Radius.full,
    backgroundColor: Colors.accent + '22',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stepNum: { fontFamily: Fonts.bold, fontSize: 14, color: Colors.accent },
  stepBody: { flex: 1 },
  stepTitle: { fontFamily: Fonts.semiBold, fontSize: 14, color: Colors.textBright, marginBottom: 4 },
  stepText: { fontFamily: Fonts.regular, fontSize: 13, color: Colors.textSecondary, lineHeight: 20 },

  /* FAQ */
  faqList: { gap: 8, marginBottom: 24 },
  faqCard: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    padding: 14,
  },
  faqCardOpen: { borderColor: Colors.accent + '55' },
  faqRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  faqIconBubble: {
    width: 32,
    height: 32,
    borderRadius: Radius.md,
    backgroundColor: Colors.accent + '18',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  faqQuestion: {
    fontFamily: Fonts.semiBold,
    fontSize: 13,
    color: Colors.textBright,
    flex: 1,
    lineHeight: 18,
  },
  faqAnswer: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    color: Colors.textSecondary,
    lineHeight: 20,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
  },

  /* Contact & Support */
  contactList: {
    gap: 0,
    marginBottom: 24,
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    overflow: 'hidden',
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  contactRowLast: { borderBottomWidth: 0 },
  contactIconBubble: {
    width: 36,
    height: 36,
    borderRadius: Radius.md,
    backgroundColor: Colors.accent + '18',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  contactTextGroup: { flex: 1 },
  contactTitle: {
    fontFamily: Fonts.semiBold,
    fontSize: 14,
    color: Colors.textBright,
  },
  contactSub: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textMuted,
    marginTop: 1,
  },

  /* Tip box */
  tipBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: Colors.accent + '12',
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.accent + '30',
    padding: 14,
  },
  tipText: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    color: Colors.textSecondary,
    lineHeight: 20,
    flex: 1,
  },
});
